<script setup lang="ts">
import { computed, nextTick, ref, watch, onMounted, onUnmounted } from "vue";
import { RouterLink, RouterView, useRoute, useRouter } from "vue-router";
import { getAccessToken } from "./services/api.js";
import { t } from "./i18n.js";
import { useNotificationSummary } from "./composables/useNotificationSummary.js";
import {
  PWA_OFFLINE_READY_EVENT,
  PWA_UPDATE_READY_EVENT,
  activateWaitingServiceWorker,
} from "./pwa/registerServiceWorker.js";
import {
  PWA_INSTALL_PROMPT_AVAILABLE_EVENT,
  clearCapturedPwaInstallPromptEvent,
  getCapturedPwaInstallPromptEvent,
} from "./pwa/installPrompt.js";

const PWA_OFFLINE_READY_BANNER_STORAGE_KEY = "wishlist:pwa-offline-ready-banner-seen";
const PWA_INSTALL_BANNER_STORAGE_KEY = "wishlist:pwa-install-banner-hidden";
const isPwaOfflineReadyBannerEnabled = import.meta.env.VITE_SHOW_PWA_OFFLINE_READY_BANNER === "true";

const route = useRoute();
const router = useRouter();

const showHeader = computed(() => route.meta.showHeader === true);
const showBottomNav = computed(() => route.meta.showBottomNav !== false);
const showBottomNavContentPadding = computed(() => (
  showBottomNav.value && route.meta.mainBottomPadding !== false
));
const isWishListNavigationBusy = ref(false);
const isAddMenuOpen = ref(false);
const isHeaderSearchOpen = ref(false);
const headerSearchQuery = ref("");
const headerSearchInputRef = ref<HTMLInputElement | null>(null);
const headerSearchFormRef = ref<HTMLFormElement | null>(null);
const headerSearchToggleRef = ref<HTMLButtonElement | null>(null);
const {
  unreadCount: unreadNotificationCount,
  refreshNotificationSummary,
  startNotificationSummaryPolling,
  stopNotificationSummaryPolling,
} = useNotificationSummary();
const currentPath = computed(() => route.path || "");
const isOffline = ref(false);
const showPwaOfflineReadyBanner = ref(false);
const isPwaUpdateReady = ref(false);
const waitingServiceWorker = ref<ServiceWorker | null>(null);
const deferredInstallPromptEvent = ref<BeforeInstallPromptEvent | null>(null);
const isPwaInstalled = ref(false);
const isPwaInstallAvailabilityChecked = ref(false);
const isPwaInstallBannerDismissed = ref(false);
const hasTriggeredControllerReload = ref(false);

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

interface InstalledRelatedApplication {
  id?: string;
  platform?: string;
  url?: string;
  version?: string;
}

interface NavigatorWithInstalledRelatedApps extends Navigator {
  getInstalledRelatedApps?: () => Promise<InstalledRelatedApplication[]>;
}

/**
 * Read whether the offline-ready banner has already been shown.
 *
 * @returns {boolean} true when the banner should stay hidden
 */
function hasSeenOfflineReadyBanner() {
  try {
    return window.localStorage.getItem(PWA_OFFLINE_READY_BANNER_STORAGE_KEY) === "true";
  } catch (error) {
    return false;
  }
}

/**
 * Persist that the offline-ready banner was already shown once.
 */
function markOfflineReadyBannerAsSeen() {
  try {
    window.localStorage.setItem(PWA_OFFLINE_READY_BANNER_STORAGE_KEY, "true");
  } catch (error) {
    // Ignore storage failures and keep the session functional.
  }
}

/**
 * Read whether the install banner was permanently hidden by the user.
 *
 * @returns {boolean} true when the install banner should stay hidden
 */
function hasHiddenPwaInstallBanner() {
  try {
    return window.localStorage.getItem(PWA_INSTALL_BANNER_STORAGE_KEY) === "true";
  } catch (error) {
    return false;
  }
}

/**
 * Persist the user choice to stop showing the install banner.
 */
function markPwaInstallBannerAsHidden() {
  try {
    window.localStorage.setItem(PWA_INSTALL_BANNER_STORAGE_KEY, "true");
  } catch (error) {
    // Ignore storage failures and still hide the banner for the current session.
  }
}

/**
 * Check whether the route path starts with a given prefix.
 *
 * @param {string} prefix Route prefix
 * @returns {boolean} true when path starts with prefix
 */
function startsWithPath(prefix: string) {
  return currentPath.value.startsWith(prefix);
}

/**
 * Check whether the route path matches a public profile namespace.
 *
 * @returns {boolean} true when path belongs to a public profile section
 */
function isPublicProfilePath() {
  return /^\/u\/[^/]+(?:\/|$)/.test(currentPath.value);
}

/**
 * Check whether the route path matches a specific public profile section.
 *
 * @param {string} section Public profile section
 * @returns {boolean} true when path targets the section
 */
function isPublicProfileSectionPath(section: string) {
  const escapedSection = section.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const matcher = new RegExp(`^\\/u\\/[^/]+\\/${escapedSection}(?:\\/|$)`);
  return matcher.test(currentPath.value);
}

/**
 * Check whether the route path targets a public wishlist or wish detail.
 *
 * @returns {boolean} true when the current public path is a wishlist path
 */
function isPublicWishlistPath() {
  return /^\/u\/[^/]+\/wishlist(?:\/|$)/.test(currentPath.value) ||
    /^\/u\/[^/]+\/wishes(?:\/|$)/.test(currentPath.value);
}

const isHeaderSearchActive = computed(() => currentPath.value === "/search");
const isHeaderNotificationsActive = computed(() => startsWithPath("/me/notifications"));
const isHeaderEventsActive = computed(() => (
  startsWithPath("/me/events") || isPublicProfileSectionPath("events")
));

const isBottomHomeActive = computed(() => (
  currentPath.value === "/feed" ||
  startsWithPath("/me/stories") ||
  isPublicProfileSectionPath("stories")
));
const isBottomWishListsActive = computed(() => (
  startsWithPath("/me/wishlist") ||
  startsWithPath("/me/wishes") ||
  isPublicWishlistPath()
));
const isBottomCommunityActive = computed(() => startsWithPath("/me/friends"));
const isBottomProfileActive = computed(() => (
  currentPath.value === "/me/profile" ||
  currentPath.value === "/me/edit" ||
  startsWithPath("/me/notifications/preferences") ||
  startsWithPath("/me/sizes") ||
  startsWithPath("/me/support/bug-report") ||
  (isPublicProfilePath() &&
    !isPublicWishlistPath() &&
    !isPublicProfileSectionPath("stories") &&
    !isPublicProfileSectionPath("events"))
));
const showPwaInstallBanner = computed(() => (
  isPwaInstallAvailabilityChecked.value &&
  !isPwaInstalled.value &&
  !isPwaInstallBannerDismissed.value &&
  deferredInstallPromptEvent.value !== null
));

watch(
  () => route.fullPath,
  () => {
    isAddMenuOpen.value = false;
    refreshNotificationSummary();
  }
);

/**
 * Navigate to the current user's default wish list detail.
 */
async function goToMyWishListDetail() {
  if (isWishListNavigationBusy.value) {
    return;
  }

  if (!getAccessToken()) {
    await router.push({
      path: "/auth/login",
      query: { redirect: "/me/wishlist" },
    });
    return;
  }

  isWishListNavigationBusy.value = true;
  try {
    await router.push("/me/wishlist");
  } catch (error) {
    console.error(error);
  } finally {
    isWishListNavigationBusy.value = false;
  }
}

/**
 * Navigate to the current user's wish creation view.
 */
async function goToWishListItemCreate() {
  closeAddMenu();
  if (isWishListNavigationBusy.value) {
    return;
  }

  if (!getAccessToken()) {
    await router.push({
      path: "/auth/login",
      query: { redirect: "/me/wishlist" },
    });
    return;
  }

  isWishListNavigationBusy.value = true;
  try {
    await router.push("/me/wishes/create");
  } catch (error) {
    console.error(error);
  } finally {
    isWishListNavigationBusy.value = false;
  }
}

/**
 * Toggle the add menu visibility.
 */
function toggleAddMenu() {
  isAddMenuOpen.value = !isAddMenuOpen.value;
}

/**
 * Close the add menu.
 */
function closeAddMenu() {
  isAddMenuOpen.value = false;
}

/**
 * Navigate to the add friend flow from the menu.
 */
function goToAddFriend() {
  closeAddMenu();
  router.push("/me/friends/add");
}

/**
 * Navigate to the create event view from the menu.
 */
function goToAddEvent() {
  closeAddMenu();
  router.push("/me/events/create");
}

/**
 * Navigate to the create story view from the menu.
 */
function goToAddStory() {
  closeAddMenu();
  router.push("/me/stories/create");
}

/**
 * Navigate to the notifications view from the header.
 */
function goToNotifications() {
  router.push("/me/notifications");
}

/**
 * Navigate to the events list from the header or bottom bar.
 */
function goToEvents() {
  router.push("/me/events");
}

/**
 * Open the header search input and select any existing text.
 */
async function openHeaderSearch() {
  isHeaderSearchOpen.value = true;
  await nextTick();
  if (headerSearchInputRef.value) {
    headerSearchInputRef.value.focus();
    headerSearchInputRef.value.select();
  }
}

/**
 * Close the header search input without clearing the text.
 */
function closeHeaderSearch() {
  isHeaderSearchOpen.value = false;
}

/**
 * Handle header search submission or input reveal.
 */
async function submitHeaderSearch() {
  if (!isHeaderSearchOpen.value) {
    await openHeaderSearch();
    return;
  }
  const trimmed = headerSearchQuery.value.trim();
  if (!trimmed) {
    return;
  }
  const types = ["person", "wishlist", "item", "story", "event"].join(",");
  await router.push({ path: "/search", query: { q: trimmed, types } });
  closeHeaderSearch();
}

/**
 * Close the header search when clicking outside of the form.
 */
function handleDocumentClick(event: MouseEvent) {
  if (!isHeaderSearchOpen.value) {
    return;
  }
  const target = event.target as Node | null;
  if (!target) {
    return;
  }
  if (headerSearchToggleRef.value && headerSearchToggleRef.value.contains(target)) {
    return;
  }
  if (headerSearchFormRef.value && headerSearchFormRef.value.contains(target)) {
    return;
  }
  closeHeaderSearch();
}

/**
 * Handle service worker update availability.
 *
 * @param {Event} event Browser event
 */
function handlePwaUpdateReady(event: Event) {
  const customEvent = event as CustomEvent<{ waitingWorker?: ServiceWorker }>;
  waitingServiceWorker.value = customEvent.detail?.waitingWorker || null;
  isPwaUpdateReady.value = true;
}

/**
 * Track service worker readiness for offline usage.
 */
function handlePwaOfflineReady() {
  if (!isPwaOfflineReadyBannerEnabled || hasSeenOfflineReadyBanner()) {
    return;
  }
  markOfflineReadyBannerAsSeen();
  showPwaOfflineReadyBanner.value = true;
}

/**
 * Check whether the current browsing context already runs as an installed PWA.
 *
 * @returns {boolean} true when the app is opened from an installed surface
 */
function isRunningAsInstalledPwa() {
  const standaloneMediaQuery = window.matchMedia?.("(display-mode: standalone)");
  return standaloneMediaQuery?.matches === true || document.referrer.startsWith("android-app://");
}

/**
 * Ask the browser whether this web app is already installed as a related app.
 *
 * @returns {Promise<boolean>} true when a related installed PWA is detected
 */
async function hasInstalledRelatedPwa() {
  const navigatorWithRelatedApps = window.navigator as NavigatorWithInstalledRelatedApps;
  if (!navigatorWithRelatedApps.getInstalledRelatedApps) {
    return false;
  }

  try {
    const relatedApplications = await navigatorWithRelatedApps.getInstalledRelatedApps();
    return relatedApplications.some((application) => (
      application.platform === "webapp"
    ));
  } catch (error) {
    return false;
  }
}

/**
 * Refresh install visibility state before showing the browser install prompt.
 */
async function refreshPwaInstallAvailability() {
  isPwaInstallAvailabilityChecked.value = false;

  if (isRunningAsInstalledPwa()) {
    isPwaInstalled.value = true;
    deferredInstallPromptEvent.value = null;
    isPwaInstallAvailabilityChecked.value = true;
    return;
  }

  isPwaInstalled.value = await hasInstalledRelatedPwa();
  if (isPwaInstalled.value) {
    deferredInstallPromptEvent.value = null;
  }
  isPwaInstallAvailabilityChecked.value = true;
}

/**
 * Track browser online status.
 */
function handleOnline() {
  isOffline.value = false;
}

/**
 * Track browser offline status.
 */
function handleOffline() {
  isOffline.value = true;
}

/**
 * Save browser install prompt event for later user action when the app is installable.
 *
 * @param {Event} event Browser event
 */
function savePwaInstallPromptEvent(event: Event | null) {
  if (!event) {
    return;
  }
  if (isPwaInstalled.value || isPwaInstallBannerDismissed.value) {
    clearCapturedPwaInstallPromptEvent();
    return;
  }
  deferredInstallPromptEvent.value = event as BeforeInstallPromptEvent;
}

/**
 * Save the browser install prompt emitted through the early bootstrap capture.
 *
 * @param {Event} event Browser event
 */
function handlePwaInstallPromptAvailable(event: Event) {
  const customEvent = event as CustomEvent<{ promptEvent?: Event }>;
  savePwaInstallPromptEvent(customEvent.detail?.promptEvent || null);
}

/**
 * Hide install prompt state once app is installed.
 */
function handleAppInstalled() {
  isPwaInstalled.value = true;
  isPwaInstallAvailabilityChecked.value = true;
  deferredInstallPromptEvent.value = null;
  clearCapturedPwaInstallPromptEvent();
}

/**
 * Reload once the updated service worker becomes the controller.
 */
function handleServiceWorkerControllerChange() {
  if (hasTriggeredControllerReload.value) {
    return;
  }
  hasTriggeredControllerReload.value = true;
  window.location.reload();
}

/**
 * Ask service worker to activate and refresh the app.
 */
function applyPwaUpdate() {
  activateWaitingServiceWorker(waitingServiceWorker.value);
  isPwaUpdateReady.value = false;
}

/**
 * Trigger browser install prompt when available.
 */
async function installPwa() {
  if (!deferredInstallPromptEvent.value) {
    return;
  }
  await deferredInstallPromptEvent.value.prompt();
  await deferredInstallPromptEvent.value.userChoice;
  deferredInstallPromptEvent.value = null;
  clearCapturedPwaInstallPromptEvent();
}

/**
 * Hide the install banner without persisting the choice.
 */
function dismissPwaInstallBanner() {
  deferredInstallPromptEvent.value = null;
  clearCapturedPwaInstallPromptEvent();
}

/**
 * Hide the install banner and remember the choice for future sessions.
 */
function permanentlyDismissPwaInstallBanner() {
  isPwaInstallBannerDismissed.value = true;
  markPwaInstallBannerAsHidden();
  dismissPwaInstallBanner();
}

/**
 * Dismiss the offline-ready info banner.
 */
function dismissOfflineReadyBanner() {
  showPwaOfflineReadyBanner.value = false;
}

/**
 * Dismiss the update-ready banner.
 */
function dismissUpdateReadyBanner() {
  isPwaUpdateReady.value = false;
}

onMounted(() => {
  isPwaInstallBannerDismissed.value = hasHiddenPwaInstallBanner();
  isOffline.value = !window.navigator.onLine;
  refreshNotificationSummary();
  startNotificationSummaryPolling();
  document.addEventListener("click", handleDocumentClick);
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
  window.addEventListener(PWA_UPDATE_READY_EVENT, handlePwaUpdateReady as EventListener);
  window.addEventListener(PWA_OFFLINE_READY_EVENT, handlePwaOfflineReady);
  window.addEventListener(PWA_INSTALL_PROMPT_AVAILABLE_EVENT, handlePwaInstallPromptAvailable as EventListener);
  window.addEventListener("appinstalled", handleAppInstalled);
  window.navigator.serviceWorker?.addEventListener("controllerchange", handleServiceWorkerControllerChange);
  refreshPwaInstallAvailability().then(() => {
    savePwaInstallPromptEvent(getCapturedPwaInstallPromptEvent());
  });
});

onUnmounted(() => {
  stopNotificationSummaryPolling();
  document.removeEventListener("click", handleDocumentClick);
  window.removeEventListener("online", handleOnline);
  window.removeEventListener("offline", handleOffline);
  window.removeEventListener(PWA_UPDATE_READY_EVENT, handlePwaUpdateReady as EventListener);
  window.removeEventListener(PWA_OFFLINE_READY_EVENT, handlePwaOfflineReady);
  window.removeEventListener(PWA_INSTALL_PROMPT_AVAILABLE_EVENT, handlePwaInstallPromptAvailable as EventListener);
  window.removeEventListener("appinstalled", handleAppInstalled);
  window.navigator.serviceWorker?.removeEventListener("controllerchange", handleServiceWorkerControllerChange);
});
</script>

<template>
  <div class="wl-app-shell">
    <header v-if="showHeader" class="wl-app-header">
      <div class="wl-container wl-header-row">
        <h1
          v-if="!isHeaderSearchOpen"
          class="text-xl font-semibold tracking-tight"
        >
          <RouterLink
            id="app_header_title"
            to="/"
            class="text-inherit no-underline"
          >
            Wish List
          </RouterLink>
        </h1>
        <form
          v-else
          id="app_header_search_form"
          ref="headerSearchFormRef"
          class="flex w-full items-center gap-2"
          @click.stop
          @submit.prevent="submitHeaderSearch"
        >
          <input
            id="app_header_search_input"
            ref="headerSearchInputRef"
            v-model="headerSearchQuery"
            class="wl-input flex-1"
            type="search"
            :placeholder="t('search.headerPlaceholder')"
            :aria-label="t('search.searchInputLabel')"
          />
          <button
            id="app_header_search_submit"
            type="submit"
            class="wl-header-action"
            :aria-label="t('search.submit')"
          >
            🔍
          </button>
        </form>
        <div v-if="!isHeaderSearchOpen" id="app_header_actions" class="wl-header-actions">
          <button
            id="app_header_search"
            ref="headerSearchToggleRef"
            type="button"
            :class="['wl-header-action', { 'wl-header-action-active': isHeaderSearchActive }]"
            :aria-label="t('header.search')"
            @click.stop
            @click="submitHeaderSearch"
          >
            🔍
          </button>
          <button
            id="app_header_notifications"
            type="button"
            :class="['wl-header-action', { 'wl-header-action-active': isHeaderNotificationsActive }]"
            :aria-label="t('header.notifications')"
            @click="goToNotifications"
          >
            🔔
            <span
              v-if="unreadNotificationCount > 0"
              id="app_header_notifications_badge"
              class="ml-2 inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-[rgb(var(--wl-primary))] px-2 py-1 text-xs font-semibold text-white"
            >
              {{ unreadNotificationCount }}
            </span>
          </button>
          <button
            id="app_header_events"
            type="button"
            :class="['wl-header-action', { 'wl-header-action-active': isHeaderEventsActive }]"
            :aria-label="t('header.events')"
            @click="goToEvents"
          >
            📅
          </button>
        </div>
      </div>
    </header>

    <main :class="['wl-app-main', { 'wl-app-main-with-bottom-nav': showBottomNavContentPadding }]">
      <div
        v-if="isOffline"
        id="app_offline_banner"
        class="mx-auto mb-3 w-full max-w-4xl rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900"
      >
        {{ t("pwa.offlineMode") }}
      </div>
      <div
        v-if="showPwaOfflineReadyBanner"
        id="app_pwa_offline_ready_banner"
        class="mx-auto mb-3 w-full max-w-4xl rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
      >
        <p id="app_pwa_offline_ready_message">{{ t("pwa.offlineReady") }}</p>
        <button
          id="app_pwa_offline_ready_dismiss"
          type="button"
          class="mt-2 rounded-full border border-emerald-800 px-3 py-1 text-xs font-semibold"
          @click="dismissOfflineReadyBanner"
        >
          {{ t("pwa.dismiss") }}
        </button>
      </div>
      <div
        v-if="isPwaUpdateReady"
        id="app_pwa_update_banner"
        class="mx-auto mb-3 w-full max-w-4xl rounded-xl border border-[rgb(var(--wl-primary))] bg-white px-4 py-3 text-sm text-[rgb(var(--wl-text))]"
      >
        <p id="app_pwa_update_message">{{ t("pwa.updateAvailable") }}</p>
        <div class="mt-2 flex gap-2">
          <button
            id="app_pwa_update_apply"
            type="button"
            class="rounded-full bg-[rgb(var(--wl-primary))] px-3 py-1 text-xs font-semibold text-white"
            @click="applyPwaUpdate"
          >
            {{ t("pwa.updateAction") }}
          </button>
          <button
            id="app_pwa_update_dismiss"
            type="button"
            class="rounded-full border border-[rgb(var(--wl-primary))] px-3 py-1 text-xs font-semibold text-[rgb(var(--wl-primary))]"
            @click="dismissUpdateReadyBanner"
          >
            {{ t("pwa.dismiss") }}
          </button>
        </div>
      </div>
      <div
        v-if="showPwaInstallBanner"
        id="app_pwa_install_banner"
        class="relative mx-auto mb-3 w-full max-w-4xl rounded-xl border border-sky-300 bg-sky-50 px-4 py-3 pr-12 text-sm text-sky-900"
      >
        <p id="app_pwa_install_message">{{ t("pwa.installPrompt") }}</p>
        <button
          id="app_pwa_install_dismiss"
          type="button"
          class="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full border border-sky-200 bg-white text-base font-semibold leading-none text-sky-800"
          :aria-label="t('pwa.dismiss')"
          @click="dismissPwaInstallBanner"
        >
          ×
        </button>
        <div class="mt-3 flex flex-wrap gap-2">
          <button
            id="app_pwa_install_action"
            type="button"
            class="rounded-full bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white"
            @click="installPwa"
          >
            {{ t("pwa.installAction") }}
          </button>
          <button
            id="app_pwa_install_do_not_show_again"
            type="button"
            class="rounded-full border border-sky-700 bg-white px-3 py-1.5 text-xs font-semibold text-sky-800"
            @click="permanentlyDismissPwaInstallBanner"
          >
            {{ t("pwa.doNotShowAgain") }}
          </button>
        </div>
      </div>
      <router-view />
    </main>

    <footer v-if="showBottomNav" id="app_bottom_footer" class="wl-app-footer">
      <div class="wl-bottom-nav-wrapper">
        <div
          v-if="isAddMenuOpen"
          id="app_add_menu_overlay"
          class="wl-add-menu-scrim"
          aria-hidden="true"
          @click="closeAddMenu"
        ></div>
      </div>
      <nav id="app_bottom_nav" class="wl-bottom-nav" aria-label="Primary">
        <button
          id="app_nav_home"
          type="button"
          :class="['wl-bottom-nav-item', { 'wl-bottom-nav-item-active': isBottomHomeActive }]"
          @click="router.push('/feed')"
        >
          <span class="wl-bottom-nav-icon" aria-hidden="true">💬</span>
            <span class="wl-bottom-nav-label">{{ t("nav.stories") }}</span>
        </button>
        <button
          id="app_nav_wishlists"
          type="button"
          :class="['wl-bottom-nav-item', { 'wl-bottom-nav-item-active': isBottomWishListsActive }]"
          :disabled="isWishListNavigationBusy"
          @click="goToMyWishListDetail"
        >
          <span class="wl-bottom-nav-icon" aria-hidden="true">🎁</span>
            <span class="wl-bottom-nav-label">{{ t("nav.wishLists") }}</span>
        </button>
        <div class="wl-bottom-nav-item-wrapper">
          <button
            id="app_nav_add"
            type="button"
            class="wl-bottom-nav-item wl-bottom-nav-action"
            :aria-expanded="isAddMenuOpen"
            aria-haspopup="true"
            @click="toggleAddMenu"
          >
            <span class="wl-bottom-nav-icon" aria-hidden="true">＋</span>
            <span class="wl-bottom-nav-label">{{ t("nav.add") }}</span>
          </button>
          <div
            v-if="isAddMenuOpen"
            id="app_add_menu"
            class="wl-add-menu-panel"
            role="menu"
            aria-labelledby="app_nav_add"
          >
            <button
              id="app_add_menu_wish"
              type="button"
              class="wl-add-menu-button"
              @click="goToWishListItemCreate"
            >
              {{ t("addMenu.wish") }}
            </button>
            <button
              id="app_add_menu_story"
              type="button"
              class="wl-add-menu-button"
              @click="goToAddStory"
            >
              {{ t("addMenu.story") }}
            </button>
            <button
              id="app_add_menu_event"
              type="button"
              class="wl-add-menu-button"
              @click="goToAddEvent"
            >
              {{ t("addMenu.event") }}
            </button>
            <button
              id="app_add_menu_friend"
              type="button"
              class="wl-add-menu-button"
              @click="goToAddFriend"
            >
              {{ t("addMenu.friend") }}
            </button>
          </div>
        </div>
        <button
          id="app_nav_community"
          type="button"
          :class="['wl-bottom-nav-item', { 'wl-bottom-nav-item-active': isBottomCommunityActive }]"
          @click="router.push('/me/friends')"
        >
          <span class="wl-bottom-nav-icon" aria-hidden="true">👥</span>
          <span class="wl-bottom-nav-label">{{ t("nav.friends") }}</span>
        </button>
        <button
          id="app_nav_profile"
          type="button"
          :class="['wl-bottom-nav-item', { 'wl-bottom-nav-item-active': isBottomProfileActive }]"
          @click="router.push('/me/profile')"
        >
          <span class="wl-bottom-nav-icon" aria-hidden="true">🙂</span>
            <span class="wl-bottom-nav-label">{{ t("nav.profile") }}</span>
        </button>
      </nav>
    </footer>
  </div>
</template>
